import React, { useState } from 'react';
import classNames from 'classnames';
import { chunk } from 'lodash';
import { Icon, Spinner } from 'ui';
import { routes } from 'constants/config';
import useFetch from 'components/shared/hooks/useFetch';
import QuestionMetricsTableRow from './QuestionMetricsTableRow';
import ModuleLoadFailure from './ModuleLoadFailure';

const performanceBarChartClass = questionMetric => {
  if (questionMetric.hasAnswers && !questionMetric.allAnswersGraded)
    return 'PerformanceChart-bar--awaitingGrade';
  if (questionMetric.hasAnswers && questionMetric.lowPerforming)
    return 'PerformanceChart-bar--lowPerforming';
  if (!questionMetric.hasAnswers) return 'PerformanceChart-bar--noResponses';
  return '';
};

const ScorableQuestions = ({ lessonIdString, currentGroupId }) => {
  const [frame, setFrame] = useState(0);
  const { data, loading, error, runRequest } = useFetch(
    routes.LESSON_OVERVIEW.ALL_QUESTION_METRICS(
      lessonIdString,
      'scorable',
      currentGroupId,
    ),
  );
  if (loading) return <Spinner />;
  if (error) {
    return <ModuleLoadFailure reload={runRequest} />;
  }
  const { questionMetrics, questionsPerFrame } = data;
  const totalFrames =
    questionMetrics.length > questionsPerFrame
      ? Math.ceil(questionMetrics.length / questionsPerFrame)
      : 1;
  return (
    <div>
      <nav>
        <h2 className="SectionHeading">{t('lesson_performance.title')}</h2>
      </nav>
      <section className="Module Module--bordered">
        <div className="PerformanceChart">
          <ul className="PerformanceChart-scale">
            <li>100%</li>
            <li>50%</li>
            <li>0%</li>
          </ul>
          <div className="PerformanceChart-data">
            {questionMetrics.length > questionsPerFrame && frame > 0 && (
              <button
                type="button"
                className="u-resetBtn PerformanceChart-nav"
                onClick={() => setFrame(frame - 1)}
              >
                <Icon name="previous" />
              </button>
            )}
            {chunk(questionMetrics, questionsPerFrame).map(
              (questionMetricPage, index) => (
                <ul
                  // eslint-disable-next-line react/no-array-index-key
                  key={`questionMetricPage${index}`}
                  className={`PerformanceChart-page ${classNames({
                    'is-active': index === frame,
                  })}`}
                >
                  {questionMetricPage.map(questionMetric => (
                    <li
                      className="PerformanceChart-measurement"
                      key={questionMetric.question.id}
                    >
                      <div
                        id={`performance_${questionMetric.question.modelName}_${questionMetric.question.id}`}
                        className="PerformanceChart-meter has_popover"
                        data-behavior="expandable collapse_on_outside_click"
                      >
                        <a
                          href={routes.LESSON_OVERVIEW.QUESTION_METRICS(
                            lessonIdString,
                            questionMetric.question.id,
                            'performance',
                            currentGroupId,
                          )}
                          className="expand_button"
                          data-behavior="one_click_popover"
                        >
                          <div
                            className={`PerformanceChart-bar ${performanceBarChartClass(
                              questionMetric,
                            )}`}
                            style={{
                              height: questionMetric.percentCorrect,
                            }}
                          />
                        </a>
                      </div>
                      <span className="PerformanceChart-baseline">
                        {questionMetric.question.position}
                      </span>
                    </li>
                  ))}
                </ul>
              ),
            )}
            {questionMetrics.length > questionsPerFrame &&
              frame < totalFrames - 1 && (
                <button
                  type="button"
                  className="u-resetBtn PerformanceChart-nav"
                  onClick={() => setFrame(frame + 1)}
                >
                  <Icon name="next" />
                </button>
              )}
          </div>
        </div>

        <table className="PerformanceTable" id="lesson-performance-table">
          <thead>
            <tr>
              <th className="PerformanceTable-rowHead">#</th>
              <th>{t('questions.title')}</th>
              <th>% {t('grades.correct')}</th>
            </tr>
          </thead>
          <tbody>
            {questionMetrics.map(questionMetric => (
              <QuestionMetricsTableRow
                key={questionMetric.question.id}
                questionMetric={questionMetric}
                lessonId={lessonIdString}
                currentGroupId={currentGroupId}
              />
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default props => <ScorableQuestions {...props} />;
